import { CSSProperties, FC, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Input, Button, Tooltip, Segmented, theme } from 'antd';
import { BoldOutlined } from '@ant-design/icons';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { ErrorText, MarkdownText } from '../Elements';
import FormLabel from './FormLabel';

const { TextArea } = Input;
const { useToken } = theme;

interface MarkdownTextAreaFieldProps {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  rows?: number;
  maxLength?: number;
  showCount?: boolean;
  formDirection?: 'row' | 'column';
  showToolbar?: boolean;
  showPreview?: boolean;
  inputStyle?: CSSProperties;
}

// Textarea with a Write/Preview toggle, a "B" button + Ctrl/Cmd+B shortcut
// that wraps the current selection with **...**. Intentionally minimal —
// bold + line breaks are the only formatting the renderer supports.
const MarkdownTextAreaField: FC<MarkdownTextAreaFieldProps> = ({
  name,
  control,
  label,
  placeholder,
  error,
  required = false,
  disabled = false,
  readonly = false,
  rows = 3,
  maxLength = 2000,
  showCount = true,
  formDirection = 'column',
  showToolbar = true,
  showPreview = true,
  inputStyle,
}) => {
  const taRef = useRef<TextAreaRef | null>(null);
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const { token } = useToken();
  const errorStatus = error ? 'error' : undefined;

  const wrapSelection = (
    currentValue: string,
    onChange: (v: string) => void,
  ) => {
    const el = taRef.current?.resizableTextArea?.textArea;
    const value = currentValue ?? '';
    if (!el) {
      onChange(`${value}**bold**`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const hasSelection = end > start;
    const selected = hasSelection ? value.slice(start, end) : 'bold';
    const next =
      value.slice(0, start) + '**' + selected + '**' + value.slice(end);
    onChange(next);

    requestAnimationFrame(() => {
      el.focus();
      const innerStart = start + 2;
      const innerEnd = innerStart + selected.length;
      el.setSelectionRange(innerStart, innerEnd);
    });
  };

  // Approximate textarea content height so Preview doesn't collapse.
  const previewMinHeight = Math.max(rows, 2) * 22 + 8;

  return (
    <div className={`form-control ${formDirection}`}>
      {label && <FormLabel required={required} label={label} />}
      <div className="input-containter">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <>
              {(showPreview || showToolbar) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                    gap: 8,
                  }}
                >
                  {showPreview ? (
                    <Segmented
                      size="small"
                      value={mode}
                      onChange={(v) => setMode(v as 'write' | 'preview')}
                      options={[
                        { label: 'Write', value: 'write' },
                        { label: 'Preview', value: 'preview' },
                      ]}
                    />
                  ) : (
                    <span />
                  )}
                  {showToolbar && (
                    <Tooltip title="Bold (Ctrl/Cmd+B)">
                      <Button
                        size="small"
                        icon={<BoldOutlined />}
                        disabled={disabled || readonly || mode === 'preview'}
                        onMouseDown={(e) => {
                          // prevent stealing focus from the textarea
                          e.preventDefault();
                        }}
                        onClick={() =>
                          wrapSelection(field.value ?? '', field.onChange)
                        }
                      />
                    </Tooltip>
                  )}
                </div>
              )}

              {mode === 'write' ? (
                <TextArea
                  {...field}
                  ref={taRef}
                  placeholder={placeholder}
                  status={errorStatus}
                  disabled={disabled}
                  readOnly={readonly}
                  rows={rows}
                  maxLength={maxLength}
                  showCount={showCount}
                  style={{ ...inputStyle }}
                  onKeyDown={(e) => {
                    if (
                      (e.ctrlKey || e.metaKey) &&
                      e.key.toLowerCase() === 'b'
                    ) {
                      e.preventDefault();
                      wrapSelection(field.value ?? '', field.onChange);
                    }
                  }}
                />
              ) : (
                <div
                  style={{
                    minHeight: previewMinHeight,
                    padding: '4px 11px',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    background: token.colorBgContainerDisabled,
                    fontSize: token.fontSize,
                    lineHeight: token.lineHeight,
                    color: token.colorText,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <MarkdownText
                    value={field.value}
                    emptyText="Nothing to preview"
                  />
                </div>
              )}
            </>
          )}
        />
        {error && <ErrorText error={error} />}
      </div>
    </div>
  );
};

export default MarkdownTextAreaField;
